import { useCallback, useMemo } from 'react';

import { getProductSelections } from 'features/menu/components/product-detail/product-combo';
import { AddToCartCta } from 'features/menu/components/product-detail/product-cta/add-to-cart-cta';
import { GoNextCta } from 'features/menu/components/product-detail/product-cta/go-next-cta';
import { MissingModifierSelectionCta } from 'features/menu/components/product-detail/product-cta/missing-modifier-selection-cta';
import {
  ActionsWrapper,
  ContentWrapper,
  StickyFooter,
} from 'features/menu/components/product-detail/product-cta/styled';
import { UpdateItemCta } from 'features/menu/components/product-detail/product-cta/update-item-cta';
import { getRequiredModifierInformation } from 'features/menu/components/product-detail/product-cta/utils';
import { ComboSlotType } from 'features/menu/components/product-detail/product-customization/types';
import { getComboSlotType } from 'features/menu/components/product-detail/product-customization/utils';
import {
  isCustomization,
  transformProductWizardStepToEventAttr,
} from 'features/menu/components/product-detail/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { DEFAULT_SLOT_KEY, ProductWizardStep } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { capitalize } from 'lodash-es';

import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isItem } from 'state/unified-menu/utils';

export const ProductCta = () => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { slotsSelections, menuObject } = useProductWizardContext();
  const { currentFlowStep, nextFlowStep } = useProductWizardFlow();
  const { posServiceMode } = useMenuQueryOptions();

  const modifierInformation = useMemo(() => {
    const shouldComputeRequiredModifiers =
      (currentFlowStep.step === ProductWizardStep.ProductHome && isItem(menuObject)) ||
      isCustomization(currentFlowStep.step);

    if (!shouldComputeRequiredModifiers) {
      return null;
    }

    const slotKey = currentFlowStep.slotKey || DEFAULT_SLOT_KEY;
    const slotSelection = slotsSelections?.[slotKey];
    const selectedItemId = slotSelection?.itemId;

    if (!selectedItemId) {
      return null;
    }

    return getRequiredModifierInformation(
      indexedMenu,
      selectedItemId,
      slotSelection,
      posServiceMode
    );
  }, [
    currentFlowStep.slotKey,
    currentFlowStep.step,
    indexedMenu,
    menuObject,
    slotsSelections,
    posServiceMode,
  ]);

  const renderCta = useCallback(() => {
    if (modifierInformation && modifierInformation.modifierRemainingSelections > 0) {
      return <MissingModifierSelectionCta modifierInformation={modifierInformation} />;
    }
    if (currentFlowStep.step === ProductWizardStep.ProductCustomization) {
      const slotSelection = slotsSelections?.[currentFlowStep.slotKey!];
      const item = indexedMenu.find(slotSelection?.itemId ?? '');

      const selections = slotSelection
        ? getProductSelections(indexedMenu, posServiceMode, item, slotSelection.modifiers)
        : [];

      const eventAttributes = {
        name: item?.name?.locale ?? '',
        Id: item?.id ?? '',
        modifierSelections: selections.map(selection => selection.name) || [],
      };

      return <UpdateItemCta eventAttributes={eventAttributes} />;
    }
    if (nextFlowStep) {
      const comboSlotType: ComboSlotType = getComboSlotType(indexedMenu, currentFlowStep.slotKey!);

      const selectedItem = slotsSelections?.[currentFlowStep?.slotKey];
      const item = selectedItem ? indexedMenu.find(selectedItem.itemId) : null;

      const eventAttributes = {
        currentStep: transformProductWizardStepToEventAttr(
          currentFlowStep.step === ProductWizardStep.ProductHome
            ? ProductWizardStep.ProductHome
            : ProductWizardStep.PickerCustomization
        ),
        comboSlotType: [ComboSlotType.SIDES, ComboSlotType.DRINKS].includes(comboSlotType)
          ? capitalize(comboSlotType.toString() ?? '')
          : '',
        Item: item?.name?.locale ?? '',
      };

      return <GoNextCta eventAttributes={eventAttributes} />;
    }
    return <AddToCartCta />;
  }, [
    currentFlowStep.slotKey,
    currentFlowStep.step,
    indexedMenu,
    modifierInformation,
    nextFlowStep,
    posServiceMode,
    slotsSelections,
  ]);

  return (
    <StickyFooter testID="product-cta-footer-banner">
      <ContentWrapper>
        <ActionsWrapper>{renderCta()}</ActionsWrapper>
      </ContentWrapper>
    </StickyFooter>
  );
};
